<template>
  <base-modal
    :show="show"
    size="md"
    custom-class="tw-pb-6"
    :has-close-btn="hasCloseButton"
    :auto-focus-modal="false"
    @close:modal="closeLoginModal"
  >
    <template #header>
      <div class="tw-flex tw-justify-center">
        <base-icon
          icon="modiologo"
          class="tw-w-36 xs:tw-w-40 tw-fill-current tw-shrink-0"
          :class="{ 'tw-text-white': DARK_MODE }"
        />
      </div>
    </template>
    <div class="tw-w-full">
      <h2 class="tw-text-center tw-mb-4 tw-text-h6 sm:tw-text-h5">
        Log in or sign up with Email
      </h2>
      <div class="tw-px-0 xs:tw-px-8 sm:tw-px-12 tw-mb-8">
        <form
          class="tw-pt-2 tw-space-y-2 xs:tw-space-y-4"
          @submit.prevent="codeInput ? sendCode : login"
        >
          <base-input
            :model-value="email"
            :errors="validationErrors.email"
            :placeholder="t('email_placeholder')"
            name="email"
            type="email"
            :max="formValidation.email.max"
            :focus="!codeInput"
            @input="(input) => updateField('email', input)"
          />

          <div v-if="codeInput" class="tw-space-y-1">
            <div class="xs:tw-px-4 tw-mb-3 xs:tw-mb-4">
              <span class="xs:tw-text-md">
                {{ t('check_email') }}
              </span>
            </div>

            <base-input
              v-if="!codeExpired"
              :model-value="code"
              :errors="validationErrors.code"
              :placeholder="t('code_placeholder')"
              :max="formValidation.code.size"
              :focus="codeInput"
              @input="(input) => updateField('code', input)"
            />
          </div>

          <base-button
            v-if="codeInput && !codeExpired"
            full-width
            primary
            :status="status"
            :disabled="!hasCodeInput"
            type="submit"
            @click="login"
          >
            Login
          </base-button>

          <div v-else class="tw-flex tw-space-x-4">
            <base-button
              v-if="!codeInput"
              class="tw-flex-1"
              primary
              full-width
              @click="toggleCode"
            >
              {{ t('have_code') }}
            </base-button>

            <base-button
              :status="status"
              class="tw-flex-1"
              primary
              :disabled="!hasEmailInput || !isObjEmpty(validationErrors.email)"
              type="submit"
              full-width
              @click="sendCode"
            >
              {{ codeExpired ? t('send_new_code') : t('email_code') }}
            </base-button>
          </div>

          <panel-error :message="apiErrors" />
        </form>
      </div>

      <div class="tw-w-full tw-flex tw-flex-col tw-items-center tw-text-center">
        <!-- <base-link class="tw-text-sm tw-mb-2" @click="goBack">
          Back to log in options
        </base-link> -->

        <term-privacy-modal-link />
      </div>
    </div>
  </base-modal>
</template>
<script>
import TermPrivacyModalLink from '@components/Modals/TermPrivacyModalLink.vue'
import { useValidate, useToggle, useStatus, useAsync } from '@composables'
import { loginConfig as formValidation } from '@config/validation.js'
import { requestCodeRequest, loginRequest } from '@services'
import PanelError from '@components/Error/PanelError.vue'
import { isObjEmpty } from '@helpers/utils.js'
import { DARK_MODE } from '@config/game.js'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    TermPrivacyModalLink,
    PanelError,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    hasCloseButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close:loginmodal', 'show:showloginoptions', 'login:success'],
  setup(_, { emit }) {
    const email = ref('')
    const tempEmail = ref('')
    const code = ref('')
    const { t } = useI18n()

    const {
      show: codeInput,
      open: showCodeInput,
      close: hideCodeInput,
    } = useToggle()
    const {
      errors: validationErrors,
      validate,
      updateErrors,
      hasErrors,
    } = useValidate(formValidation.validation)

    const {
      error: codeErrors,
      loading: codeLoading,
      run: requestCodeRun,
      setError: setCodeErrors,
    } = useAsync(() => requestCodeRequest({ email: email.value }))

    const {
      error: loginErrors,
      loading: loginLoading,
      run: loginRun,
      setError: setLoginErrors,
    } = useAsync(() => loginRequest({ code: code.value }))

    const isLoading = computed(() => codeLoading.value || loginLoading.value)
    const apiErrors = computed(() =>
      codeErrors.value ? t('code_error') : loginErrors.value
    )

    const codeExpired = computed(() =>
      apiErrors.value?.includes('code has expired')
    )
    const hasEmailInput = computed(() => email.value !== '')
    const hasCodeInput = computed(() => code.value !== '')

    const { status, updateStatus, statusType } = useStatus({
      loading: isLoading,
      error: apiErrors,
    })

    async function sendCode() {
      _clearApiErrors()
      code.value = ''

      if (email.value) {
        validate('email', email.value)

        if (hasErrors('email')) return

        await requestCodeRun()
        if (codeErrors.value) {
          updateErrors(codeErrors.value)
        } else {
          showCodeInput()
          tempEmail.value = email.value
          updateStatus(statusType.SUCCESS)
        }
      }
    }

    async function login() {
      if (loginLoading.value) return

      _clearApiErrors()
      validate('code', code.value)
      validate('email', email.value)

      if (hasErrors()) return

      await loginRun()

      if (!loginErrors.value) {
        closeLoginModal()
        emit('login:success')
      } else {
        tempEmail.value = email.value
      }
    }

    function updateField(field, input) {
      _clearApiErrors()
      if (field === 'email') {
        if (!isObjEmpty(validationErrors.email)) {
          validate(field, input)
        }
        email.value = input
        if (codeInput.value) {
          checkTempEmail()
        }
      }

      if (field === 'code') {
        if (
          input.length === 0 ||
          input.length > 5 ||
          !isObjEmpty(validationErrors.code)
        ) {
          validate(field, input)
        }
        code.value = input
        if (input.length === 5) {
          setTimeout(login, 500)
        }
      }
    }

    function toggleCode() {
      showCodeInput()
      code.value = ''
    }

    function checkTempEmail() {
      if (tempEmail.value && tempEmail.value !== email.value) {
        hideCodeInput()
        code.value = ''
        tempEmail.value = ''
      }
    }

    function _clearApiErrors() {
      setCodeErrors(null)
      setLoginErrors(null)
    }

    function closeLoginModal() {
      _clearApiErrors()
      email.value = null
      tempEmail.value = null
      code.value = null
      hideCodeInput()
      emit('close:loginmodal')
    }

    function goBack() {
      _clearApiErrors()
      emit('show:showloginoptions')
    }

    return {
      validationErrors,
      closeLoginModal,
      formValidation,
      hasEmailInput,
      hasCodeInput,
      codeExpired,
      updateField,
      toggleCode,
      isObjEmpty,
      DARK_MODE,
      codeInput,
      apiErrors,
      sendCode,
      goBack,
      status,
      login,
      email,
      code,
      t,
    }
  },
}
</script>
